import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Checkbox, Snackbar, Alert } from '@mui/material';
import { fetchCurrentSchedules, fetchScheduleTemplates, amendSchedules } from '../../../service/scheduleAmendmentsService';
import { useParams } from 'react-router-dom';
import './ScheduleAmendmentsPage.css';
import SnackBar from '../../../component/snackbarContent/SnackBar';
import { useTranslation } from "react-i18next";
import { getTranslationForDataGrid } from '../../../utils/Utils';


const ScheduleAmendmentsPage = () => {
  const { t, i18n } = useTranslation();
  const { contractNumber } = useParams();
  const [isLoading, setIsLoading] = useState(true); 
  const [currentSchedules, setCurrentSchedules] = useState([]);
  const [scheduleTemplates, setScheduleTemplates] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: '', errorType: "tryAndCatch" });
  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', type: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const schedules = await fetchCurrentSchedules(contractNumber, t);
        setCurrentSchedules(schedules);

        const templates = await fetchScheduleTemplates(contractNumber,t);
        setScheduleTemplates(templates.rows);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        setSnackbar({ open: true, message: 'Something went wrong. Please try again later.', type: 'error' });
      } finally {
        setIsLoading(false); 
      }
    };

    fetchData();
  }, [contractNumber]);

  const handleScheduleSelect = (scheduleId) => {
    setSelectedSchedules((prev) =>
      prev.includes(scheduleId)
        ? prev.filter((id) => id !== scheduleId) // Uncheck
        : [...prev, scheduleId] // Check
    );
  };

  const handleAmendSchedules = async () => {
    if (selectedSchedules.length === 0) return;

    setIsLoading(true); 
    try {
      const response = await amendSchedules(contractNumber, selectedSchedules);

      if (response) {
        setSnackbar({ open: true, message: 'Schedules amended successfully!', type: 'success' });
        setSelectedSchedules([]);

        // Refetch current schedules
        const updatedSchedules = await fetchCurrentSchedules(contractNumber, t);
        setCurrentSchedules(updatedSchedules);
      }
    } catch (error) {
      console.error("Error amending schedules:", error);
      setSnackbar({ open: true, message: 'Failed to amend schedules. Please try again.', type: 'error' });
      setSelectedSchedules([]);
    } finally {
      setIsLoading(false); 
    }
  };


  return (
    <div className="schedule-amendments-page">
      <h1 className="page-heading">{t("Schedule Amendments for Contract")} {contractNumber}</h1>
      <hr className="horizontal-ruler" />

      {/* Current Schedules Section */}
      <div className="current-schedules">
        <h2>{t("Current Schedules Amended")}</h2>
        <DataGrid
          rows={currentSchedules}
          columns={[
            { field: 'schedule_number', headerName: t('Schedule Number'), width: 150 },
            { field: 'schedule_pdf_name', headerName: t('Schedule Name'), width: 300 },
          ]}
          pageSize={5}
          autoHeight
          localeText={getTranslationForDataGrid(i18n.resolvedLanguage)}
        />
      </div>

      {/* Choose Schedules Section */}
      <div className="choose-schedules">
        <h2>{t("Choose a Schedule to Amend:")}</h2>
        <DataGrid
          rows={scheduleTemplates}
          columns={[
            { field: 'id', headerName: t('Schedule Number'), width: 200 },
            { field: 'schedulename', headerName: t('Schedule Name'), width: 300 },
            { field: 'pro_rate_amount', headerName: t('Prorate Amount'), width: 200 },
            {
              field: 'select',
              headerName: t('Select'),
              width: 200,
              renderCell: (params) => (
                <Checkbox
                  checked={selectedSchedules.includes(params.row.id)}
                  onChange={() => handleScheduleSelect(params.row.id)}
                />
              ),
            },
          ]}
          pageSize={5}
          autoHeight
          localeText={getTranslationForDataGrid(i18n.resolvedLanguage)}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleAmendSchedules}
          disabled={isLoading || selectedSchedules.length === 0} 
          style={{ marginTop: '10px' }}
        >
          {isLoading ? "LOADING..." : t("AMEND SCHEDULE")}
        </Button>

        <SnackBar handleCloseSnackbar={handleCloseSnackbar} snackbar={snackbar} />
      </div>
    </div>
  );
};

export default ScheduleAmendmentsPage;
